import { createRoute, lazyRouteComponent } from "@tanstack/react-router";
import { UserRole, UserType } from "@/types";
import { createProtectedLayoutRoute } from "./protectedLayouts";
import {
  assetOverviewSearchSchema,
  assetOverviewSearchMiddlewares,
  customersSearchSchema,
  customersSearchMiddlewares,
  declareDissentSearchSchema,
  investmentSearchSchema,
  investmentSearchMiddlewares,
  manageUsersSearchSchema,
  manageUsersSearchMiddlewares,
  poolActivitySearchSchema,
  poolActivitySearchMiddlewares,
  transactionsSearchSchema,
  transactionsSearchMiddlewares,
} from "@/routing/search";

const AssetOverview = lazyRouteComponent(() => import("@/pages/shared/AssetOverview/Page"));
const CustomersOverview = lazyRouteComponent(() => import("@/pages/user/CustomersOverview/Page"));
const Dashboard = lazyRouteComponent(() => import("@/pages/user/Dashboard/Page"));
const DeclareDissent = lazyRouteComponent(() => import("@/pages/user/DeclareDissent/Page"));
const Investment = lazyRouteComponent(() => import("@/pages/user/Investment/Page"));
const MakePayment = lazyRouteComponent(() => import("@/pages/user/MakePayment/Page"));
const ManageUsers = lazyRouteComponent(() => import("@/pages/shared/ManageUsers/Page"));
const PoolActivity = lazyRouteComponent(() => import("@/pages/shared/PoolActivity/Page"));
const Portfolio = lazyRouteComponent(() => import("@/pages/user/Portfolio/Page"));
const Settings = lazyRouteComponent(() => import("@/pages/user/Settings/Page"));
const WalletSetup = lazyRouteComponent(() => import("@/pages/user/WalletSetup/Page"));
const Transactions = lazyRouteComponent(() => import("@/pages/shared/Transactions/Page"));

const userEmployeeFinancierLayout = createProtectedLayoutRoute(
  "user-employee-financier",
  [UserRole.User, UserRole.Superuser],
  [UserType.Employee, UserType.Financier],
);

export const userDashboardRoute = createRoute({
  getParentRoute: () => userEmployeeFinancierLayout,
  path: "/user/dashboard",
  component: Dashboard,
});

export const userAssetOverviewRoute = createRoute({
  getParentRoute: () => userEmployeeFinancierLayout,
  path: "/user/assetoverview",
  validateSearch: assetOverviewSearchSchema,
  search: { middlewares: assetOverviewSearchMiddlewares },
  component: AssetOverview,
});

const userFinancierLayout = createProtectedLayoutRoute(
  "user-financier",
  [UserRole.User, UserRole.Superuser, UserRole.Admin],
  [UserType.Financier],
);

export const userInvestmentRoute = createRoute({
  getParentRoute: () => userFinancierLayout,
  path: "/user/investment",
  validateSearch: investmentSearchSchema,
  search: { middlewares: investmentSearchMiddlewares },
  component: Investment,
});

export const userPortfolioRoute = createRoute({
  getParentRoute: () => userFinancierLayout,
  path: "/user/portfolio",
  component: Portfolio,
});

export const userPoolActivityRoute = createRoute({
  getParentRoute: () => userFinancierLayout,
  path: "/user/poolactivity",
  validateSearch: poolActivitySearchSchema,
  search: { middlewares: poolActivitySearchMiddlewares },
  component: PoolActivity,
});

const userSuperuserLayout = createProtectedLayoutRoute(
  "user-superuser",
  [UserRole.Superuser],
  [UserType.Financier, UserType.Employee],
);

export const userManageUsersRoute = createRoute({
  getParentRoute: () => userSuperuserLayout,
  path: "/user/manageusers",
  validateSearch: manageUsersSearchSchema,
  search: { middlewares: manageUsersSearchMiddlewares },
  component: ManageUsers,
});

export const userTransactionsRoute = createRoute({
  getParentRoute: () => userSuperuserLayout,
  path: "/user/transactions",
  validateSearch: transactionsSearchSchema,
  search: { middlewares: transactionsSearchMiddlewares },
  component: Transactions,
});

const userSettingsLayout = createProtectedLayoutRoute(
  "user-settings",
  [UserRole.User, UserRole.Superuser, UserRole.Admin],
  [UserType.Financier, UserType.Employee, UserType.Developer],
);

export const userSettingsRoute = createRoute({
  getParentRoute: () => userSettingsLayout,
  path: "/user/settings",
  component: Settings,
});

export const userWalletSetupRoute = createRoute({
  getParentRoute: () => userSettingsLayout,
  path: "/user/wallet-setup",
  component: WalletSetup,
});

const userEmployeeLayout = createProtectedLayoutRoute(
  "user-employee",
  [UserRole.User, UserRole.Superuser],
  [UserType.Employee],
);

// TODO: Customers page disabled
export const userCustomersOverviewRoute = createRoute({
  getParentRoute: () => userEmployeeLayout,
  path: "/user/customersoverview",
  validateSearch: customersSearchSchema,
  search: { middlewares: customersSearchMiddlewares },
  component: CustomersOverview,
});

export const userMakePaymentRoute = createRoute({
  getParentRoute: () => userEmployeeLayout,
  path: "/user/makepayment",
  component: MakePayment,
});

export const declareDissentRoute = createRoute({
  getParentRoute: () => userEmployeeLayout,
  path: "/declare-dissent",
  validateSearch: declareDissentSearchSchema,
  component: DeclareDissent,
});

export const userRoutes = [
  userEmployeeFinancierLayout.addChildren([
    userDashboardRoute,
    userAssetOverviewRoute,
  ]),
  userFinancierLayout.addChildren([
    userInvestmentRoute,
    userPortfolioRoute,
    userPoolActivityRoute,
  ]),
  userSuperuserLayout.addChildren([
    userManageUsersRoute,
    userTransactionsRoute,
  ]),
  userSettingsLayout.addChildren([userSettingsRoute, userWalletSetupRoute]),
  userEmployeeLayout.addChildren([
    // TODO: Customers page disabled
    // userCustomersOverviewRoute,
    userMakePaymentRoute,
    declareDissentRoute,
  ]),
];
